import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";

// Exact Sidebar Menu Rounded Icon
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

const TopNavbar = ({ toggleActive }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const [isDark, setIsDark] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const checkDarkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkDarkTheme();
    const observer = new MutationObserver(checkDarkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    if (isDark) {
      htmlElement.classList.remove("dark-theme");
      bodyElement.classList.remove("dark-theme");
      setIsDark(false);
    } else {
      htmlElement.classList.add("dark-theme");
      bodyElement.classList.add("dark-theme");
      setIsDark(true);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(err);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      component="header"
      className="header-area top-navbar"
      sx={{
        width: "100%",
        height: "48px",
        minHeight: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        margin: 0,
        padding: 0,
        pl: 0,
        pr: { xs: 1.5, md: 2.5 },
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.96)" : "rgba(255, 255, 255, 0.96)",
        backdropFilter: "blur(12px)",
        borderBottom: isDark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid #E2E8F0",
        boxSizing: "border-box",
        position: "relative",
        zIndex: 1000,
        transition: "all 0.2s ease",
      }}
    >
      {/* LEFT: EXACT THREE LINES BURGER & CLINICAL EHR BADGE */}
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: { xs: 1, sm: 1.5 } }}>
        <Box
          onClick={toggleActive}
          sx={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "50px",
            height: "100%",
            margin: 0,
            borderRight: isDark
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid #E2E8F0",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 98, 255, 0.02)",
            color: isDark ? "#CBD5E1" : "#334155",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(56, 189, 248, 0.1)"
                : "rgba(0, 98, 255, 0.06)",
              color: isDark ? "#38BDF8" : "#0062FF",
            },
          }}
        >
          <MenuRoundedIcon sx={{ fontSize: "1.5rem" }} />
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
            px: 1.2,
            py: 0.35,
            borderRadius: "6px",
            backgroundColor: isDark ? "rgba(16, 185, 129, 0.08)" : "#F0FDF4",
            border: isDark
              ? "1px solid rgba(16, 185, 129, 0.2)"
              : "1px solid #BBF7D0",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#10B981",
              boxShadow: "0 0 6px #10B981",
            }}
          />
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.4px",
              color: isDark ? "#34D399" : "#15803D",
              textTransform: "uppercase",
            }}
          >
            HIPAA Compliant Session
          </Typography>
        </Box>
      </Box>

      {/* RIGHT: COMPACT CONTROLS & PROFILE */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1.1 } }}>
        <Tooltip title={isDark ? "Activate Day Mode" : "Activate Night Mode"}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.08)"
                : "1px solid #E2E8F0",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#0062FF",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.07)" : "#F8FAFC",
              },
            }}
          >
            {isDark ? (
              <LightModeOutlinedIcon sx={{ fontSize: "1.15rem", color: "#FBBF24" }} />
            ) : (
              <DarkModeOutlinedIcon sx={{ fontSize: "1.15rem", color: "#475569" }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Clinical Alerts">
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.08)"
                : "1px solid #E2E8F0",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
              position: "relative",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#0062FF",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.07)" : "#F8FAFC",
              },
            }}
          >
            <NotificationsNoneOutlinedIcon
              sx={{ fontSize: "1.2rem", color: isDark ? "#CBD5E1" : "#475569" }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#EF4444",
                boxShadow: "0 0 5px #EF4444",
              }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen View"}>
          <IconButton
            onClick={toggleFullscreen}
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.08)"
                : "1px solid #E2E8F0",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
              display: { xs: "none", sm: "flex" },
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#0062FF",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.07)" : "#F8FAFC",
              },
            }}
          >
            {isFullscreen ? (
              <FullscreenExitOutlinedIcon
                sx={{ fontSize: "1.2rem", color: isDark ? "#CBD5E1" : "#475569" }}
              />
            ) : (
              <FullscreenOutlinedIcon
                sx={{ fontSize: "1.2rem", color: isDark ? "#CBD5E1" : "#475569" }}
              />
            )}
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            height: "22px",
            width: "1px",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#E2E8F0",
            mx: 0.3,
            display: { xs: "none", sm: "block" },
          }}
        />

        <Box
          onClick={handleProfileClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: "3px 10px 3px 5px",
            borderRadius: "10px",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid #E2E8F0",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
            boxShadow: isDark
              ? "0 4px 14px rgba(0, 0, 0, 0.25)"
              : "0 2px 6px rgba(15, 23, 42, 0.04)",
            cursor: "pointer",
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: isDark ? "#38BDF8" : "#0062FF",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "#F8FAFC",
            },
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Avatar
              sx={{
                width: 30,
                height: 30,
                background: "linear-gradient(135deg, #0052FF 0%, #4F46E5 100%)",
                fontSize: "0.78rem",
                fontWeight: 800,
                color: "#FFFFFF",
              }}
            >
              U
            </Avatar>
            <Box
              sx={{
                position: "absolute",
                bottom: -1,
                right: -1,
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#10B981",
                border: isDark ? "1.5px solid #0f172a" : "1.5px solid #FFFFFF",
              }}
            />
          </Box>

          <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  color: isDark ? "#FFFFFF" : "#0F172A",
                  lineHeight: 1.1,
                }}
              >
                Dr. Usman
              </Typography>
              <Chip
                label="MD"
                size="small"
                sx={{
                  height: "15px",
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  backgroundColor: isDark ? "rgba(56, 189, 248, 0.15)" : "#EFF6FF",
                  color: isDark ? "#38BDF8" : "#0062FF",
                  borderRadius: "3px",
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.66rem",
                fontWeight: 600,
                color: isDark ? "#94A3B8" : "#64748B",
                lineHeight: 1.1,
                mt: 0.15,
              }}
            >
              Lead Neurologist
            </Typography>
          </Box>

          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: "1rem",
              color: isDark ? "#94A3B8" : "#64748B",
              transition: "transform 0.2s ease",
              transform: openMenu ? "rotate(180deg)" : "rotate(0deg)",
              ml: { xs: 0, sm: 0.3 },
            }}
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 210,
              borderRadius: "12px",
              boxShadow: isDark
                ? "0 14px 35px rgba(0, 0, 0, 0.5)"
                : "0 10px 30px rgba(15, 23, 42, 0.08)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.08)"
                : "1px solid #E2E8F0",
              backgroundColor: isDark ? "#15203c" : "#FFFFFF",
              color: isDark ? "#FFFFFF" : "#0F172A",
              p: 0.8,
              "& .MuiMenuItem-root": {
                fontSize: "0.82rem",
                fontWeight: 600,
                py: 0.8,
                px: 1.2,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255, 255, 255, 0.06)"
                    : "#F1F5F9",
                },
              },
            },
          }}
        >
          <Box sx={{ px: 1.2, py: 0.6, mb: 0.3 }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>
              Active Clinical Session
            </Typography>
            <Typography sx={{ fontSize: "0.84rem", fontWeight: 800, color: isDark ? "#FFFFFF" : "#0F172A" }}>
              Central Hospital Ward A
            </Typography>
          </Box>

          <Divider sx={{ my: 0.4, borderColor: isDark ? "rgba(255,255,255,0.06)" : "#EEF2F6" }} />

          <MenuItem component={Link} to="/dashboard/patient-profile">
            <PersonOutlineOutlinedIcon sx={{ fontSize: "1.15rem", color: "#64748B" }} />
            Physician Profile
          </MenuItem>

          <MenuItem component={Link} to="/dashboard/security">
            <VerifiedUserOutlinedIcon sx={{ fontSize: "1.15rem", color: "#10B981" }} />
            HIPAA Security Logs
          </MenuItem>

          <MenuItem component={Link} to="/dashboard/settings">
            <SettingsOutlinedIcon sx={{ fontSize: "1.15rem", color: "#64748B" }} />
            Portal Settings
          </MenuItem>

          <Divider sx={{ my: 0.5, borderColor: isDark ? "rgba(255,255,255,0.06)" : "#EEF2F6" }} />

          <MenuItem
            component={Link}
            to="/authentication/logout/"
            sx={{
              color: "#EF4444 !important",
              "&:hover": {
                backgroundColor: "rgba(239, 68, 68, 0.08) !important",
              },
            }}
          >
            <LogoutOutlinedIcon sx={{ fontSize: "1.15rem", color: "#EF4444" }} />
            Secure Sign Out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default TopNavbar;